import useBreakPoints from 'hooks/useBreakPoints';
import React, { ReactNode, useRef } from 'react';
import Slider, { Settings } from 'react-slick';
import styled, { useTheme } from 'styled-components';
import Carousel from './Carousel';
import { NextArrow, PrevArrow } from './CarouselArrows';
import Grid, { GridChild } from '../Grid';

export type SlidesConfig = {
    mobile: number;
    tablet: number;
    desktop: number;
    wide?: number;
    default: number;
};

export type CardCarouselProps = {
    children: ReactNode[];
    slidesConfig: SlidesConfig;
    $gap?: string;
    controls?: ReactNode;
    footer?: ReactNode;
    showDefaultArrows?: boolean;
    nextArrow?: React.ReactElement;
    prevArrow?: React.ReactElement;
    centerPadding?: string;
    arrowVerticalPosition?: string;
    staggered?: boolean;
    fullWidth?: boolean;
    centered?: boolean;
    centerMode?: boolean;
    infinite?: boolean;
    autoplay?: boolean;
    onItemClick?: () => void;
    onBeforeChange?: () => void;
    onAfterChange?: (index: number) => void;
    sliderRef?: React.RefObject<Slider>;
};

const DEFAULT_SLIDES_CONFIG: SlidesConfig = {
    mobile: 2,
    tablet: 3,
    desktop: 4,
    wide: 5,
    default: 5,
};

const DEFAULT_GAP = '8px';
const MOBILE_GAP = '2px';

export const CardCarousel = ({
    children,
    slidesConfig = DEFAULT_SLIDES_CONFIG,
    $gap,
    controls,
    footer,
    centerPadding,
    showDefaultArrows = true,
    nextArrow: nextArrowProp,
    prevArrow: prevArrowProp,
    arrowVerticalPosition = '50%',
    staggered = false,
    fullWidth = false,
    centered = false,
    centerMode = false,
    infinite = false,
    autoplay = false,
    onItemClick,
    onBeforeChange,
    onAfterChange,
    sliderRef,
}: CardCarouselProps) => {
    const { width } = useBreakPoints();
    const theme = useTheme();
    const hasDraggedRef = useRef(false);

    const { breakpoints } = theme;
    const itemCount = children.length;

    const handleCardClick: React.MouseEventHandler<HTMLElement> = (e) => {
        if (hasDraggedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            hasDraggedRef.current = false;
            return;
        }
        onItemClick?.();
    };

    const handleBeforeChange = () => {
        hasDraggedRef.current = true;
        onBeforeChange?.();
    };

    const handleAfterChange = (index: number) => {
        setTimeout(() => {
            hasDraggedRef.current = false;
        }, 0);
        onAfterChange?.(index);
    };

    const getCurrentSlidesToShow = (): number => {
        if (width <= breakpoints.sm) return slidesConfig.mobile;
        if (width <= breakpoints.md) return slidesConfig.tablet;
        if (width <= breakpoints.lg) return slidesConfig.desktop;
        if (slidesConfig.wide && width <= breakpoints.xxl) return slidesConfig.wide;
        return slidesConfig.default;
    };

    const currentSlidesToShow = getCurrentSlidesToShow();
    const gap = $gap ?? (width <= breakpoints.sm ? MOBILE_GAP : DEFAULT_GAP);
    const carouselSettings: Settings = {
        arrows: showDefaultArrows && itemCount > slidesConfig.default,
        infinite,
        speed: autoplay ? 300 : 100,
        swipeToSlide: true,
        autoplay,
        centerMode,
        centerPadding,
        autoplaySpeed: 5000,
        slidesToScroll: 1,
        touchThreshold: 100,
        beforeChange: handleBeforeChange,
        afterChange: handleAfterChange,
        nextArrow: showDefaultArrows ? nextArrowProp ?? <NextArrow /> : undefined,
        prevArrow: showDefaultArrows ? prevArrowProp ?? <PrevArrow /> : undefined,
        responsive: [
            {
                breakpoint: breakpoints.sm,
                settings: {
                    slidesToShow: slidesConfig.mobile,
                    slidesToScroll: 1,
                    swipeToSlide: true,
                    arrows: false,
                },
            },
            {
                breakpoint: breakpoints.md,
                settings: {
                    slidesToShow: slidesConfig.tablet,
                    arrows: showDefaultArrows && itemCount > slidesConfig.tablet,
                },
            },
            {
                breakpoint: breakpoints.lgmd,
                settings: {
                    slidesToShow: slidesConfig.desktop,
                    arrows: showDefaultArrows && itemCount > slidesConfig.desktop,
                },
            },
            ...(slidesConfig.wide
                ? [
                      {
                          breakpoint: breakpoints.xxl,
                          settings: {
                              slidesToShow: slidesConfig.wide,
                              arrows: showDefaultArrows && itemCount > slidesConfig.wide,
                          },
                      },
                  ]
                : []),
        ],
        slidesToShow: slidesConfig.default,
    };

    if (!itemCount) {
        return null;
    }

    // If items fit without scrolling, render as a simple grid instead of carousel
    if (itemCount <= currentSlidesToShow) {
        const gridColumns = fullWidth ? currentSlidesToShow : itemCount;
        return (
            <>
                <CarouselGridContainer
                    $columns={gridColumns}
                    $gap={gap}
                    $centered={centered}
                    $fullWidth={fullWidth}
                    $maxColumns={currentSlidesToShow}
                >
                    {children.map((child, i) => (
                        <CardWrapper
                            key={i}
                            $staggered={staggered}
                            $index={i}
                            onClick={handleCardClick}
                            draggable={false}
                        >
                            {child}
                        </CardWrapper>
                    ))}
                </CarouselGridContainer>
                {footer}
            </>
        );
    }

    return (
        <SliderContainer $arrowPosition={arrowVerticalPosition}>
            <Carousel ref={sliderRef} gap={gap} {...carouselSettings}>
                {children.map((child, i) => (
                    <CardWrapper
                        key={i}
                        $staggered={staggered}
                        $index={i}
                        onClickCapture={handleCardClick}
                        draggable={false}
                    >
                        {child}
                    </CardWrapper>
                ))}
            </Carousel>
            <FooterRow columns={{ base: '1fr', sm: '1fr auto 1fr' }} align="center">
                <GridChild col="1 / -1" justify="center">
                    {footer}
                </GridChild>
                <GridChild col="-2 / -1" justify="end">
                    {controls}
                </GridChild>
            </FooterRow>
        </SliderContainer>
    );
};

const SliderContainer = styled.div<{ $arrowPosition: string }>`
    .slick-track {
        display: flex !important;
    }

    .slick-slide {
        height: auto;
        align-self: stretch;

        > div {
            height: 100%;
        }

        // Prevent native drag on all elements inside slides
        a,
        img,
        button {
            -webkit-user-drag: none;
            user-drag: none;
        }
    }

    .slick-prev.slick-disabled,
    .slick-next.slick-disabled {
        opacity: 0;
        pointer-events: none;
    }

    .slick-prev,
    .slick-next {
        top: ${({ $arrowPosition }) => $arrowPosition};
        transform: translateY(-50%);
        z-index: 1;

        &::before {
            display: none;
        }
    }

    .slick-slider {
        position: relative;
    }
`;

const CardWrapper = styled.div<{ $staggered?: boolean; $index?: number }>`
    user-select: none;
    height: 100%;
    ${({ $staggered, $index = 0, theme }) => {
        if (!$staggered) return '';
        // Odd indices are positioned lower, but only on larger screens
        const offset = $index % 2 === 1 ? '48px' : '0px';
        return `
      @media (min-width: ${theme.breakpoints.md}px) {
        padding-top: ${offset};
      }
    `;
    }}
`;

const CarouselGridContainer = styled.div<{
    $columns: number;
    $gap: string;
    $centered?: boolean;
    $fullWidth?: boolean;
    $maxColumns?: number;
}>`
    display: grid;
    grid-template-columns: ${({ $columns, $fullWidth, $maxColumns, $gap }) =>
        $fullWidth || !$maxColumns
            ? `repeat(${$columns}, 1fr)`
            : `repeat(${$columns}, calc((100% - ${$maxColumns - 1} * ${$gap}) / ${$maxColumns}))`};
    gap: ${({ $gap }) => $gap};
    width: 100%;
    ${({ $centered }) => $centered && 'justify-content: center;'}

    ${({ theme }) => theme.media.smBelow} {
        grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
        padding-left: 12px;
        padding-right: 12px;
    }
`;

const FooterRow = styled(Grid)`
    margin-top: 12px;

    > * {
        grid-row: 1;
    }
`;

export default CardCarousel;
